import React, { useState, useEffect, useCallback } from "react";
import {
  Row, Col, Spinner,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Input, Label, FormGroup,
} from "reactstrap";
import FeatherIcon from "feather-icons-react";
import serviceControlService from "../../../services/ServiceControlService";

/* ─── Shared card style (no Card component = no theme override) ─── */
const card = {
  backgroundColor: "#ffffff",
  borderRadius:    12,
  border:          "1px solid #e2e8f0",
  boxShadow:       "0 1px 4px rgba(0,0,0,0.06)",
  transition:      "box-shadow .2s, transform .2s",
  overflow:        "hidden",
};

/* ─── Helpers ─────────────────────────────────────────────────────── */
const fmtAgo = (ts) => {
  if (!ts) return "Never";
  const diff = Math.floor((Date.now() - new Date(ts)) / 60000);
  if (diff < 1)     return "Just now";
  if (diff < 60)    return `${diff}m ago`;
  if (diff < 1440)  return `${Math.floor(diff / 60)}h ago`;
  if (diff < 10080) return `${Math.floor(diff / 1440)}d ago`;
  return new Date(ts).toLocaleDateString();
};

const statusMeta = (s) => ({
  active:          { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", label: "Active" },
  online:          { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", label: "Online" },
  disconnected:    { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", label: "Disconnected" },
  offline:         { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", label: "Offline" },
  never_connected: { color: "#94a3b8", bg: "#f8fafc", border: "#e2e8f0", label: "Never Connected" },
}[s] || { color: "#94a3b8", bg: "#f8fafc", border: "#e2e8f0", label: s || "Unknown" });

/* ─── StatusLogModal ──────────────────────────────────────────────── */
const StatusLogModal = ({ isOpen, toggle, agentName, authFetch }) => {
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !agentName) return;
    setLoading(true);
    authFetch(`/api/agents/${encodeURIComponent(agentName)}/status-log?limit=30&days=30`)
      .then(d => setEvents(d?.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [isOpen, agentName]); // eslint-disable-line

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle} style={{ borderBottom: "1px solid #f1f5f9", fontWeight: 700, backgroundColor: "#ffffff" }}>
        <FeatherIcon icon="activity" size={16} className="me-2" style={{ color: "#d87844" }} />
        Status History — <span style={{ color: "#d87844" }}>{agentName}</span>
      </ModalHeader>
      <ModalBody style={{ padding: "20px 24px", maxHeight: 420, overflowY: "auto", backgroundColor: "#ffffff" }}>
        {loading ? (
          <div className="text-center py-4"><Spinner size="sm" style={{ color: "#d87844" }} /></div>
        ) : events.length === 0 ? (
          <div className="text-center py-4" style={{ color: "#94a3b8", fontSize: 14 }}>
            <FeatherIcon icon="inbox" size={28} className="d-block mx-auto mb-2" />
            No status events recorded yet
          </div>
        ) : (
          <div>
            {events.map((ev, i) => {
              const online = ev.event_type === "online";
              return (
                <div key={i} style={{ display: "flex", gap: 14, paddingBottom: 16, position: "relative" }}>
                  {i < events.length - 1 && (
                    <div style={{ position: "absolute", left: 10, top: 24, bottom: 0, width: 1, backgroundColor: "#f1f5f9" }} />
                  )}
                  <div style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, backgroundColor: online ? "#f0fdf4" : "#fef2f2", border: `2px solid ${online ? "#16a34a" : "#dc2626"}`, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: online ? "#16a34a" : "#dc2626" }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: online ? "#16a34a" : "#dc2626" }}>
                        {online ? "Came Online" : "Went Offline"}
                      </span>
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>{fmtAgo(ev.timestamp)}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                      {ev.from_status} → {ev.to_status}{ev.agent_ip ? ` · ${ev.agent_ip}` : ""}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ModalBody>
      <ModalFooter style={{ borderTop: "1px solid #f1f5f9", backgroundColor: "#ffffff" }}>
        <button onClick={toggle} className="btn btn-sm btn-light">Close</button>
      </ModalFooter>
    </Modal>
  );
};

/* ─── ServiceActionModal ──────────────────────────────────────────── */
const ServiceActionModal = ({ modal, onClose, onConfirm }) => {
  const colorMap = { start: "#16a34a", restart: "#d97706", stop: "#dc2626" };
  const color    = colorMap[modal.action] || "#d87844";

  return (
    <Modal isOpen={modal.isOpen} toggle={onClose} centered>
      <ModalHeader toggle={onClose} style={{ borderBottom: "1px solid #f1f5f9", fontWeight: 700, backgroundColor: "#ffffff" }}>
        Confirm — {modal.action?.charAt(0).toUpperCase()}{modal.action?.slice(1)} Service
      </ModalHeader>
      <ModalBody style={{ padding: "24px", backgroundColor: "#ffffff" }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ width: 56, height: 56, borderRadius: "50%", backgroundColor: `${color}14`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <FeatherIcon icon={modal.action === "start" ? "play" : modal.action === "restart" ? "refresh-cw" : "square"} size={24} style={{ color }} />
          </div>
          <p style={{ fontSize: 14, color: "#1e293b", marginBottom: 6 }}>
            <strong>{modal.action}</strong> service on <strong>{modal.agent?.hostname || modal.agent?.name}</strong>?
          </p>
          <p style={{ fontSize: 12, color: "#94a3b8" }}>This will affect all managed processes on the endpoint.</p>
        </div>
        {serviceControlService.requiresPassword(modal.action) && (
          <FormGroup>
            <Label style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Service Password</Label>
            <Input
              type="password"
              placeholder="Enter service password"
              value={modal.password}
              onChange={e => onConfirm({ ...modal, password: e.target.value }, "update")}
              autoFocus
              style={{ borderRadius: 8, fontSize: 13 }}
            />
            {modal.passwordError && <div style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>{modal.passwordError}</div>}
          </FormGroup>
        )}
      </ModalBody>
      <ModalFooter style={{ borderTop: "1px solid #f1f5f9", gap: 8, backgroundColor: "#ffffff" }}>
        <button onClick={onClose} className="btn btn-sm btn-light">Cancel</button>
        <button
          onClick={() => onConfirm(modal, "execute")}
          style={{ backgroundColor: color, color: "#fff", border: "none", borderRadius: 8, padding: "6px 18px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
        >
          <FeatherIcon icon={modal.action === "start" ? "play" : modal.action === "restart" ? "refresh-cw" : "square"} size={13} className="me-1" />
          {modal.action?.charAt(0).toUpperCase()}{modal.action?.slice(1)}
        </button>
      </ModalFooter>
    </Modal>
  );
};

/* ─── Main ────────────────────────────────────────────────────────── */
const AgentControlTable = () => {
  const [agents,        setAgents]        = useState([]);
  const [wazuhAgents,   setWazuhAgents]   = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [credError,     setCredError]     = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [successMsg,    setSuccessMsg]    = useState(null);
  const [search,        setSearch]        = useState("");
  const [filter,        setFilter]        = useState("all");
  const [statusModal,   setStatusModal]   = useState({ isOpen: false, agentName: null });
  const [confirmModal,  setConfirmModal]  = useState({ isOpen: false, action: null, agent: null, password: "", passwordError: "" });

  const creds = () => ({ apiUrl: localStorage.getItem("flaskApiUrl"), apiKey: localStorage.getItem("flaskApiKey") });

  const authFetch = useCallback(async (path, opts = {}) => {
    const { apiUrl, apiKey } = creds();
    if (!apiUrl || !apiKey) throw new Error("Credentials not configured");
    const r = await fetch(`${apiUrl}${path}`, {
      ...opts,
      headers: { "Content-Type": "application/json", "X-API-Key": apiKey, ...(opts.headers || {}) },
    });
    if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || `HTTP ${r.status}`); }
    return r.json();
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setCredError(false);
    try {
      const { apiUrl, apiKey } = creds();
      if (!apiUrl || !apiKey) { setCredError(true); throw new Error("Credentials not configured"); }

      const [agentsData, wazuhData] = await Promise.all([
        authFetch("/api/agents"),
        authFetch("/api/wazuh/agents").catch(() => ({ success: false, agents: [] })),
      ]);

      if (agentsData?.agents) {
        setAgents(agentsData.agents.map(a => ({
          id: a.agent_id, hostname: a.hostname || a.agent_id,
          agentId: a.agent_id, os: a.os_info || "Unknown",
          status: a.status || "unknown", lastSeen: a.last_seen, ip: a.ip_address || "—",
        })));
      }
      if (wazuhData?.agents) setWazuhAgents(wazuhData.agents);
    } catch (e) {
      setError(e.message);
      if (e.message.includes("Credentials") || e.message.includes("Authentication")) setCredError(true);
    } finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const enriched = agents.map(a => {
    const w = wazuhAgents.find(w => w.name === a.hostname || w.name === a.agentId);
    return { ...a, wazuhStatus: w?.status, wazuhOs: w?.os, wazuhVersion: w?.version };
  });

  const filtered = enriched.filter(a => {
    const ms = !search || a.hostname.toLowerCase().includes(search.toLowerCase()) || a.ip.includes(search);
    const mf = filter === "all"
      || (filter === "active" && a.status === "online")
      || (filter === "disconnected" && a.status !== "online");
    return ms && mf;
  });

  const openConfirm  = (agent, action) => setConfirmModal({ isOpen: true, action, agent, password: "", passwordError: "" });
  const closeConfirm = () => setConfirmModal({ isOpen: false, action: null, agent: null, password: "", passwordError: "" });

  const handleConfirm = async (modal, type) => {
    if (type === "update") { setConfirmModal(modal); return; }
    const { agent, action, password } = modal;
    if (serviceControlService.requiresPassword(action) && !password) {
      setConfirmModal(m => ({ ...m, passwordError: "Password is required" })); return;
    }
    setActionLoading(p => ({ ...p, [agent.id]: action }));
    closeConfirm(); setSuccessMsg(null); setError(null);
    try {
      const r = await serviceControlService.executeServiceControl(agent.id, action, password, () => {});
      if (r.success) { setSuccessMsg(`Service ${action} completed on ${agent.hostname}`); setTimeout(load, 2000); }
      else throw new Error(r.data?.error || `Failed to ${action}`);
    } catch (e) { setError(e.message); }
    finally { setActionLoading(p => { const n = { ...p }; delete n[agent.id]; return n; }); }
  };

  if (loading) return (
    <div style={{ ...card, padding: "48px 24px", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Spinner style={{ color: "#d87844" }} />
      <span style={{ marginLeft: 12, color: "#94a3b8", fontSize: 14 }}>Loading agents...</span>
    </div>
  );

  return (
    <>
      {/* Toast messages */}
      {successMsg && (
        <div style={{ padding: "10px 16px", backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, fontSize: 13, color: "#16a34a", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span><FeatherIcon icon="check-circle" size={14} className="me-2" />{successMsg}</span>
          <button onClick={() => setSuccessMsg(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 0 }}>✕</button>
        </div>
      )}
      {error && !credError && (
        <div style={{ padding: "10px 16px", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, fontSize: 13, color: "#dc2626", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span><FeatherIcon icon="alert-circle" size={14} className="me-2" />{error}</span>
          <button onClick={() => setError(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 0 }}>✕</button>
        </div>
      )}
      {credError && (
        <div style={{ padding: "12px 16px", backgroundColor: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, fontSize: 13, color: "#c2410c", marginBottom: 12 }}>
          <FeatherIcon icon="alert-triangle" size={14} className="me-2" />
          API credentials missing.{" "}
          <button onClick={() => { localStorage.removeItem("flaskApiUrl"); localStorage.removeItem("flaskApiKey"); window.location.href = "/login"; }}
            style={{ background: "none", border: "none", color: "#d87844", fontWeight: 600, cursor: "pointer", padding: 0, textDecoration: "underline" }}>
            Return to login
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#1e293b" }}>Deployed Agents</span>
          <span style={{ fontSize: 12, padding: "2px 10px", backgroundColor: "#f1f5f9", borderRadius: 20, color: "#64748b", fontWeight: 600 }}>
            {filtered.length}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div style={{ position: "relative" }}>
            <FeatherIcon icon="search" size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }} />
            <input
              type="text" placeholder="Search agents..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ paddingLeft: 30, paddingRight: 12, height: 34, border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, color: "#1e293b", outline: "none", minWidth: 180, backgroundColor: "#ffffff" }}
            />
          </div>
          {["all", "active", "disconnected"].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              height: 34, padding: "0 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
              backgroundColor: filter === f ? "#d87844" : "#ffffff",
              color:            filter === f ? "#fff"    : "#64748b",
              border:           filter === f ? "1px solid #d87844" : "1px solid #e2e8f0",
            }}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
          <button onClick={load} style={{ height: 34, padding: "0 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", backgroundColor: "#ffffff", color: "#64748b", border: "1px solid #e2e8f0" }}>
            <FeatherIcon icon="refresh-cw" size={13} className="me-1" />Refresh
          </button>
        </div>
      </div>

      {/* Agent cards */}
      {filtered.length === 0 ? (
        <div style={{ ...card, padding: "48px 24px", textAlign: "center" }}>
          <FeatherIcon icon="server" size={40} style={{ color: "#cbd5e1" }} className="d-block mx-auto mb-3" />
          <p style={{ color: "#94a3b8", fontSize: 14, margin: 0 }}>
            {search || filter !== "all" ? "No agents match your filters" : "No agents deployed yet"}
          </p>
        </div>
      ) : (
        <Row className="g-3">
          {filtered.map(agent => {
            const st        = statusMeta(agent.status);
            const wSt       = agent.wazuhStatus ? statusMeta(agent.wazuhStatus) : null;
            const isLoading = !!actionLoading[agent.id];
            const curAction = actionLoading[agent.id];

            return (
              <Col xl={4} md={6} key={agent.id}>
                <div
                  style={{ ...card, height: "100%" }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,.09)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)"; e.currentTarget.style.transform = ""; }}
                >
                  <div style={{ padding: "20px 22px" }}>

                    {/* Header */}
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h6 style={{ fontWeight: 700, color: "#1e293b", marginBottom: 6, fontSize: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {agent.hostname}
                        </h6>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          {/* Service status badge */}
                          <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, padding: "2px 10px", borderRadius: 20, backgroundColor: st.bg, color: st.color, border: `1px solid ${st.border}`, fontWeight: 600 }}>
                            <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: st.color }} />
                            {st.label}
                          </span>
                          {/* Monitor status badge */}
                          {wSt && (
                            <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, padding: "2px 10px", borderRadius: 20, backgroundColor: "#f8fafc", color: "#64748b", border: "1px solid #e2e8f0" }}>
                              <span style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: wSt.color }} />
                              Monitor: {wSt.label}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* History button */}
                      <button
                        onClick={() => setStatusModal({ isOpen: true, agentName: agent.hostname })}
                        title="View status history"
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 4, marginTop: -2, flexShrink: 0 }}
                        onMouseEnter={e => e.currentTarget.style.color = "#d87844"}
                        onMouseLeave={e => e.currentTarget.style.color = "#94a3b8"}
                      >
                        <FeatherIcon icon="activity" size={15} />
                      </button>
                    </div>

                    {/* Details */}
                    <div style={{ marginBottom: 16 }}>
                      {[
                        { icon: "cpu",   label: "OS",       value: agent.wazuhOs || agent.os },
                        { icon: "globe", label: "IP",       value: agent.ip,       mono: true },
                        { icon: "tag",   label: "Agent ID", value: agent.agentId?.substring(0, 16) + (agent.agentId?.length > 16 ? "…" : ""), mono: true },
                        { icon: "clock", label: "Last Seen",value: fmtAgo(agent.lastSeen) },
                        ...(agent.wazuhVersion ? [{ icon: "info", label: "Version", value: agent.wazuhVersion }] : []),
                      ].map((row, ri, arr) => (
                        <div key={ri} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: ri < arr.length - 1 ? "1px solid #f8fafc" : "none" }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#94a3b8" }}>
                            <FeatherIcon icon={row.icon} size={12} />{row.label}
                          </span>
                          <span style={{ fontSize: 12, color: "#475569", fontFamily: row.mono ? "monospace" : "inherit" }}>
                            {row.value}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: "flex", gap: 6 }}>
                      {[
                        { action: "start",   icon: "play",       label: "Start",   color: "#16a34a" },
                        { action: "restart", icon: "refresh-cw", label: "Restart", color: "#d97706" },
                        { action: "stop",    icon: "square",     label: "Stop",    color: "#dc2626" },
                      ].map(btn => (
                        <button
                          key={btn.action}
                          onClick={() => openConfirm(agent, btn.action)}
                          disabled={isLoading || credError}
                          style={{
                            flex: 1, height: 34, borderRadius: 8, fontSize: 12, fontWeight: 600,
                            cursor: isLoading || credError ? "not-allowed" : "pointer",
                            backgroundColor: isLoading && curAction === btn.action ? `${btn.color}14` : "#f8fafc",
                            color: btn.color,
                            border: `1px solid ${btn.color}28`,
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                            transition: "all .15s",
                            opacity: isLoading || credError ? .6 : 1,
                          }}
                          onMouseEnter={e => { if (!isLoading && !credError) { e.currentTarget.style.backgroundColor = `${btn.color}14`; e.currentTarget.style.borderColor = btn.color; }}}
                          onMouseLeave={e => { if (!isLoading && !credError) { e.currentTarget.style.backgroundColor = "#f8fafc"; e.currentTarget.style.borderColor = `${btn.color}28`; }}}
                        >
                          {isLoading && curAction === btn.action
                            ? <Spinner size="sm" />
                            : <FeatherIcon icon={btn.icon} size={12} />
                          }
                          {btn.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      )}

      <StatusLogModal
        isOpen={statusModal.isOpen}
        toggle={() => setStatusModal({ isOpen: false, agentName: null })}
        agentName={statusModal.agentName}
        authFetch={authFetch}
      />
      <ServiceActionModal
        modal={confirmModal}
        onClose={closeConfirm}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export default AgentControlTable;